import {
    getTimesheetHoursMap,
    calculateActualHours,
    aggregateHoursByStage,
    calculateTasksHours,
    getVariance,
    getUtilisationRate,
    getEffortCompletionPercentage,
    formatHoursDisplay
} from '../../hours-calculation';
import { roundValue, toDecimal } from '../../../../../util/decimal-utils';


// Helper to calculate hours data for all tasks in all stages recursively
const calculateHoursRecursively = (stages, timesheetEntries, hoursMap = null) => {
    const localHoursMap = hoursMap || getTimesheetHoursMap(timesheetEntries);
    return stages.map(stage => {
        if (stage.stages && Array.isArray(stage.stages) && stage.stages.length > 0) {
            // Handle nested stages recursively
            const nestedStages = calculateHoursRecursively(stage.stages, timesheetEntries, localHoursMap);

            // Aggregate hours from all nested stages, PLUS any hours directly on this parent stage
            let totalForecastHours = toDecimal(0);
            let totalActualHours = toDecimal(calculateActualHours(stage.id, timesheetEntries, 'STAGE', localHoursMap));

            nestedStages.forEach(nestedStage => {
                totalForecastHours = totalForecastHours.plus(toDecimal(nestedStage.forecastHours || 0));
                totalActualHours = totalActualHours.plus(toDecimal(nestedStage.actualHours || 0));
            });

            const variance = getVariance(totalActualHours.toNumber(), totalForecastHours.toNumber());
            const utilisationRate = getUtilisationRate(totalActualHours.toNumber(), totalForecastHours.toNumber());
            const effortCompletionPercentage = getEffortCompletionPercentage(totalActualHours.toNumber(), totalForecastHours.toNumber());

            return {
                ...stage,
                stages: nestedStages,
                forecastHours: totalForecastHours.toNumber(),
                actualHours: totalActualHours.toNumber(),
                forecastHoursDisplay: formatHoursDisplay(totalForecastHours.toNumber()),
                actualHoursDisplay: formatHoursDisplay(totalActualHours.toNumber()),
                variance: roundValue(variance, 0),
                utilisationRate: roundValue(utilisationRate, 0),
                effortCompletionPercentageValue: effortCompletionPercentage,
                effortCompletionPercentage: `${effortCompletionPercentage.toFixed(2)}%`
            };
        } else {
            // Handle tasks at this level
            const stageTasks = stage.tasks || [];
            // Pass stage.id to include direct stage-level hours
            const stageHours = aggregateHoursByStage(stageTasks, timesheetEntries, stage.id, localHoursMap);
            const taskHoursData = calculateTasksHours(stageTasks, timesheetEntries, localHoursMap);

            return {
                ...stage,
                forecastHours: stageHours.totalForecastHours,
                actualHours: stageHours.totalActualHours,
                forecastHoursDisplay: formatHoursDisplay(stageHours.totalForecastHours),
                actualHoursDisplay: formatHoursDisplay(stageHours.totalActualHours),
                variance: roundValue(stageHours.variance, 0), // % over/under budget - whole numbers
                utilisationRate: roundValue(stageHours.utilisationRate, 0), // % of budget used - whole numbers
                effortCompletionPercentageValue: stageHours.effortCompletionPercentageValue,
                effortCompletionPercentage: stageHours.effortCompletionPercentage,

                tasks: stageTasks.map(task => ({
                    ...task,
                    ...(taskHoursData[task.id] || {})
                }))
            };
        }
    });
};

export default calculateHoursRecursively;